import { EMPTY_FIELD } from 'constants/emptyField';

import React, { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import RuleIcon from '@mui/icons-material/Rule';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { ListContextProvider, UseListValue } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { PageHeader } from 'shared/mui/PageHeader';
import { BooleanField, FunctionField, ImageField, TextField } from 'shared/react-admin/Fields';
import { Pagination } from 'shared/react-admin/Pagination/Pagination';
import { Toolbar } from 'shared/react-admin/Toolbars/Toolbar';
import { OrderMethod } from 'types/embedded/orderMethod';
import { cleanEmpty } from 'utils/cleanEmpty';

import OrderMethodsRulesShowDialog from './components/OrderMethodsRulesShowDialog/OrderMethodsRulesShowDialog';
import { useOrderMethodsContext } from './hooks/useOrderMethodsContext';
import { StyledDatagrid } from './OrderMethodsList.styled';
import { OrderMethodsListFilter } from './OrderMethodsListFilter';
import { OrderMethodsListFilterChips } from './OrderMethodsListFilterChips';
import { schema } from './schemas/schema';

export type OrderMethodsListFilter = {
  merchantId?: string;
  currency?: string;
  direction?: string;
  customerId?: string;
  country?: string;
  language?: string;
  countDeposit?: number;
  appVersion?: string;
  domain?: string;
};

const DEFAULT_ORDER_METHODS_FILTER_VALUES = {
  merchantId: null,
  currency: null,
  direction: null,
  customerId: null,
  country: null,
  language: null,
  countDeposit: null,
  appVersion: null,
  domain: null,
};

export const OrderMethodsList: React.FC = () => {
  const translate = useTypedTranslate();
  const { form, onSubmit, onReset, appliedFilters, setAppliedFilter, openDrawer, toggleDrawer } =
    useFilters<any>({
      defaultValues: DEFAULT_ORDER_METHODS_FILTER_VALUES,
      resetValues: DEFAULT_ORDER_METHODS_FILTER_VALUES,
      resolver: yupResolver(schema),
      mode: 'all',
    });

  const { context, refetch } = useOrderMethodsContext(appliedFilters);
  const [selectedOrderMethod, setSelectedOrderMethod] = useState<OrderMethod | undefined>();
  const [isOpen, setIsOpen] = useState(false);
  const isListFilterExist = Object.keys(cleanEmpty(appliedFilters)).length > 0;

  useEffect(() => {
    if (Object.keys(cleanEmpty(appliedFilters)).length > 0) {
      refetch();
    }
  }, [appliedFilters]);

  return (
    <ListContextProvider value={context as UseListValue}>
      <OrderMethodsRulesShowDialog
        onClose={() => setIsOpen(false)}
        open={isOpen}
        orderMethod={selectedOrderMethod}
      />
      <Box my={3}>
        <PageHeader headerTitle={{ titleText: translate('app.orderMethods.titleText') }} />
        <FilterToolbar
          leftActionsSlot={[<FilterButton key="filter" onClick={toggleDrawer(true)} />]}
        />
        <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
          <FormProvider {...form}>
            <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
              <FilterDrawerContent>
                <OrderMethodsListFilter />
              </FilterDrawerContent>
              <FilterDrawerActionsButtons>
                <FilterResetButton onReset={onReset} />
                <FilterApplyButton type="submit" />
              </FilterDrawerActionsButtons>
            </form>
          </FormProvider>
        </FilterDrawer>
        <FilterChipsToolbar appliedFilters={appliedFilters}>
          <FilterContextProvider
            appliedFilters={appliedFilters}
            onReset={onReset}
            setAppliedFilter={setAppliedFilter}
          >
            <FormProvider {...form}>
              <OrderMethodsListFilterChips />
              <FilterClearButton key="clear" onClick={onReset} visible={isListFilterExist} />
            </FormProvider>
          </FilterContextProvider>
        </FilterChipsToolbar>
        {isListFilterExist ? (
          <>
            <StyledDatagrid bulkActionButtons={false}>
              <TextField label={translate('app.orderMethods.methodName')} source="name" />
              <TextField label={translate('app.table.alias')} source="alias" />
              <ImageField
                emptyText={EMPTY_FIELD}
                label={translate('app.table.logo')}
                source="logo"
                sx={{
                  '& .RaImageField-image': {
                    maxWidth: 43,
                    maxHeight: 43,
                    minWidth: 43,
                    minHeight: 43,
                    objectFit: 'contain',
                  },
                }}
              />
              <TextField label={translate('app.orderMethods.methodType')} source="type.name" />
              <TextField label={translate('app.table.gateway')} source="gateway" />
              <FunctionField
                label={translate('app.orderMethods.rules')}
                render={(orderMethod: OrderMethod) => (
                  <Button
                    label={translate('app.buttons.view')}
                    onClick={() => {
                      setSelectedOrderMethod(orderMethod);
                      setIsOpen(true);
                    }}
                    size="small"
                    startIcon={<RuleIcon sx={{ fontSize: '18px' }} />}
                  />
                )}
              />
              <BooleanField
                label={translate('app.orderMethods.amountRequest')}
                source="requestFormAmount"
              />
            </StyledDatagrid>
            <Toolbar style={{ paddingLeft: 0, paddingRight: 0 }}>
              <Pagination sx={{ width: '100%' }} />
            </Toolbar>
          </>
        ) : (
          <Alert severity="info">{translate('app.orderMethods.infoAlert')}</Alert>
        )}
      </Box>
    </ListContextProvider>
  );
};
